<template>
  <li class="nav-item nav-search">
    <!-- Icon -->
    <a
      href="javascript:void(0)"
      class="nav-link nav-link-search"
      @click="showSearchBar = true"
    >
      <feather-icon icon="SearchIcon" size="21" />
    </a>

    <!-- Input -->
    <div class="search-input" :class="{ open: showSearchBar }">
      <div class="search-input-icon">
        <feather-icon icon="SearchIcon" />
      </div>
      <b-form-input
        v-if="showSearchBar"
        v-model="searchQuery"
        :placeholder="`${$t('Cerca')}...`"
        autofocus
        autocomplete="off"
        @keyup.up="increaseIndex(false)"
        @keyup.down="increaseIndex"
        @keyup.esc="
          showSearchBar = false;
          resetsearchQuery();
        "
        @keyup.enter="suggestionSelected"
        @blur.stop="
          showSearchBar = false;
          resetsearchQuery();
        "
      />
      <div
        class="search-input-close"
        @click="
          showSearchBar = false;
          resetsearchQuery();
        "
      >
        <feather-icon icon="XIcon" />
      </div>

      <!-- Suggestions List -->
      <vue-perfect-scrollbar
        :settings="perfectScrollbarSettings"
        class="search-list search-list-main scroll-area overflow-hidden"
        :class="{ show: showResultsBox }"
        tagname="ul"
      >
        <li v-if="loadingSearchResults" class="d-flex justify-content-center">
          <b-spinner variant="primary" class="m-3" />
        </li>

        <template v-else>
          <li
            v-if="emptyFilteredData"
            class="suggestion-group-suggestion no-results"
          >
            <p>Sense resultats 💩</p>
          </li>

          <template v-else>
            <li
              v-for="(suggestion_list, grp_name, grp_index) in filteredData"
              :key="grp_index"
              class="suggestions-groups-list"
            >
              <!-- Group Header -->
              <p class="suggestion-group-title">
                <span>
                  {{ getGroupTitle(grp_name) }}
                </span>
              </p>

              <!-- Suggestion List of each group -->
              <ul>
                <li
                  v-for="(suggestion, index) in suggestion_list"
                  :key="index"
                  class="suggestion-group-suggestion cursor-pointer"
                  :class="{
                    'suggestion-current-selected':
                      currentSelected === `${grp_index}.${index}`,
                  }"
                  @mouseenter="currentSelected = `${grp_index}.${index}`"
                  @mousedown.prevent="suggestionSelected(grp_name, suggestion)"
                >
                  <b-link v-if="grp_name === 'bookings'" class="p-0">
                    <div class="d-flex align-items-center">
                      <b-avatar
                        :src="getBookingSourceImage(suggestion.source)"
                        class="mr-1"
                        size="32"
                      />
                      <div>
                        <p
                          :class="{
                            'text-decoration-line-through':
                              suggestion.cancelled,
                          }"
                        >
                          {{ suggestion.localizator }} @
                          {{ suggestion.accommodation }}
                        </p>
                        <p>
                          <small>
                            {{ suggestion.client }}
                          </small>
                        </p>
                        <p>
                          <small>
                            {{ suggestion.checkin }} -
                            {{ suggestion.checkout }}
                          </small>
                        </p>
                      </div>
                    </div>
                  </b-link>
                  <b-link v-else-if="grp_name === 'accommodations'" class="p-0">
                    <div class="d-flex align-items-center">
                      <b-avatar
                        :src="suggestion.image"
                        class="mr-1"
                        size="32"
                      />
                      <div>
                        <p>{{ suggestion.name }}</p>
                        <small>{{ suggestion.city }}</small>
                      </div>
                    </div>
                  </b-link>
                </li>
                <li
                  v-if="!suggestion_list.length && searchQuery"
                  class="suggestion-group-suggestion no-results"
                >
                  <p>Sense resultats.</p>
                </li>
              </ul>
            </li>
          </template>
        </template>
      </vue-perfect-scrollbar>
    </div>
  </li>
</template>

<script>
import { BFormInput, BLink, BAvatar, BSpinner } from "bootstrap-vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { title } from "@core/utils/filter";
import _debounce from "lodash/debounce";
import { getBookingSourceImage } from "@/utils/methods";

export default {
  components: {
    BFormInput,
    BLink,
    BAvatar,
    BSpinner,
    VuePerfectScrollbar,
  },
  data() {
    return {
      showSearchBar: false,
      showResultsBox: false,
      perfectScrollbarSettings: {
        maxScrollbarLength: 60,
      },
      currentSelected: -1,
      searchQuery: "",
      searchLimit: 5,
      title,
      getBookingSourceImage,
    };
  },
  computed: {
    loadingSearchResults() {
      return this.$store.getters["app/loadingSearchResults"];
    },
    searchResults() {
      return this.$store.getters["app/searchResults"];
    },
    emptySearchResults() {
      return (
        !this.searchResults.bookings.data.length &&
        !this.searchResults.accommodations.data.length
      );
    },
    filteredData() {
      if (this.searchQuery === "" || this.emptySearchResults) {
        return {};
      } else {
        const queriedData = {};
        const dataGrps = Object.keys(this.searchResults);

        dataGrps.forEach((grp, i) => {
          if (!this.searchResults[grp].data?.length) return;
          queriedData[dataGrps[i]] = this.searchResults[grp].data.slice(
            0,
            this.searchLimit
          );
        });

        return queriedData;
      }
    },
    emptyFilteredData() {
      return Object.keys(this.filteredData).length === 0;
    },
  },
  watch: {
    searchQuery(val) {
      this.$store.commit("app/TOGGLE_OVERLAY", Boolean(val));
      if (val.length) this.fetchSearchResults(this);
    },
    showSearchBar(val) {
      if (!val) this.showResultsBox = false;
    },
    filteredData(val) {
      if (!Object.values(val).some((obj) => obj.length)) {
        this.currentSelected = -1;
      } else {
        // Auto Select first item if it's not item-404
        let grpIndex = null;

        for (const [index, grpSuggestions] of Object.values(val).entries()) {
          if (grpSuggestions.length) {
            grpIndex = index;
            break;
          }
        }

        if (grpIndex !== null) this.currentSelected = `${grpIndex}.0`;
      }
    },
  },
  methods: {
    suggestionSelected(grpName, suggestion) {
      // If parameter is not provided => Use current selected
      if (!suggestion) {
        // If currentSelected value is -1 => No value/item is selected (Prevent Errors)
        if (this.currentSelected !== -1) {
          const [grpIndex, itemIndex] = this.currentSelected.split(".");
          grpName = Object.keys(this.filteredData)[grpIndex];
          suggestion = this.filteredData[grpName][itemIndex];
        }
      }
      // if (grpName === "pages") router.push(suggestion.route).catch(() => {});
      this.$router.push(suggestion.route).catch(() => {});
      this.resetsearchQuery();
      this.showSearchBar = false;
    },
    resetsearchQuery() {
      this.searchQuery = "";
    },
    increaseIndex(val = true) {
      // If there's no matching items
      if (!Object.values(this.filteredData).some((grpItems) => grpItems.length))
        return;

      const [grpIndex, itemIndex] = this.currentSelected.split(".");

      const grpArr = Object.entries(this.filteredData);
      const activeGrpTotalItems = grpArr[grpIndex][1].length;

      if (val) {
        // If active item is not of last item in grp
        if (activeGrpTotalItems - 1 > itemIndex) {
          this.currentSelected = `${grpIndex}.${Number(itemIndex) + 1}`;

          // If active item grp is not last in grp list
        } else if (grpIndex < grpArr.length - 1) {
          for (let i = Number(grpIndex) + 1; i < grpArr.length; i++) {
            // If navigating group have items => Then move in that group
            if (grpArr[i][1].length > 0) {
              this.currentSelected = `${Number(i)}.0`;
              break;
            }
          }
        }
      } else {
        // If active item is not of first item in grp
        if (Number(itemIndex)) {
          this.currentSelected = `${grpIndex}.${Number(itemIndex) - 1}`;

          // If active item grp  is not first in grp list
        } else if (Number(grpIndex)) {
          for (let i = Number(grpIndex) - 1; i >= 0; i--) {
            // If navigating group have items => Then move in that group
            if (grpArr[i][1].length > 0) {
              this.currentSelected = `${i}.${grpArr[i][1].length - 1}`;
              break;
            }
          }
        }
      }
    },
    fetchSearchResults: _debounce((innerThis) => {
      innerThis.$store.dispatch(
        "app/fetchSearchResults",
        innerThis.searchQuery
      );
      innerThis.showResultsBox = true;
    }, 750),
    getGroupTitle(groupName) {
      switch (groupName) {
        case "bookings":
          return this.$t("Reserves");
        case "accommodations":
          return this.$t("Allotjaments");
        default:
          return title(groupName);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/bootstrap-extended/include";
@import "~@core/scss/base/components/variables-dark";

ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
p {
  margin: 0;
}

.suggestion-group-title {
  font-weight: 500;
  padding: 0.75rem 1rem 0.25rem;
}

.suggestion-group-suggestion {
  padding: 0.75rem 1rem;
}

.suggestion-current-selected {
  background-color: $body-bg;

  .dark-layout & {
    background-color: $theme-dark-body-bg;
  }
}
</style>
